import { useState, type MouseEvent } from 'react';
import {
  CheckCircle2Icon,
  DownloadIcon,
  Loader2Icon,
  XCircleIcon,
} from 'lucide-react';
import {
  documentGenerationToolInputSchema,
  documentGenerationToolOutputSchema,
} from '@shared/schemas/document-generation';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useChatExperience } from '../chat-experience';
import { buildCodegenChatDocument } from '../codegen-document';
import { getChatErrorMessage } from '../chat-errors';
import { useDocumentGenerationStatus } from '../hooks/useDocumentGeneration';
import { DocumentGenerationKindIcon } from './ChatDocumentKindIcon';
import { ChatDocumentVersionsMenu } from './ChatDocumentVersionsMenu';

type ChatDocumentGenerationToolProps = {
  errorText?: string;
  input?: unknown;
  output?: unknown;
  state: 'approval-requested'
    | 'approval-responded'
    | 'input-available'
    | 'input-streaming'
    | 'output-available'
    | 'output-denied'
    | 'output-error';
};

async function triggerFileDownload(downloadUrl: string, filename: string) {
  const response = await fetch(downloadUrl);
  if (!response.ok) {
    throw new Error('download_failed');
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function ChatDocumentGenerationTool({
  errorText,
  input,
  output,
  state,
}: ChatDocumentGenerationToolProps) {
  const { openDocument } = useChatExperience();
  const parsedInput = documentGenerationToolInputSchema.safeParse(input);
  const parsedOutput = documentGenerationToolOutputSchema.safeParse(output);
  const accepted = parsedOutput.success && parsedOutput.data.status === 'pending'
    ? parsedOutput.data
    : null;
  const statusQuery = useDocumentGenerationStatus(accepted?.conversationId, accepted?.generationId);
  const [isOpeningPreview, setIsOpeningPreview] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const resolveFreshResult = async () => {
    const fresh = await statusQuery.refetch();
    if (fresh.data?.status !== 'succeeded') {
      throw fresh.error ?? new Error('download_url_unavailable');
    }
    return fresh.data.result;
  };

  const handlePreview = async () => {
    if (isOpeningPreview || isDownloading) {
      return;
    }
    setIsOpeningPreview(true);
    try {
      const result = await resolveFreshResult();
      openDocument(buildCodegenChatDocument({
        result,
        title: accepted?.title
          ?? (parsedInput.success ? parsedInput.data.title : 'Document riche'),
      }));
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setIsOpeningPreview(false);
    }
  };

  const handleDownload = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (isDownloading || isOpeningPreview) {
      return;
    }
    setIsDownloading(true);
    try {
      const result = await resolveFreshResult();
      await triggerFileDownload(result.downloadUrl, result.filename);
    } catch (error) {
      toast.error(getChatErrorMessage(error));
    } finally {
      setIsDownloading(false);
    }
  };

  const generation = statusQuery.data ?? null;
  const title = accepted?.title
    ?? (parsedInput.success ? parsedInput.data.title : 'Document riche');
  const kind = accepted?.kind
    ?? (parsedOutput.success ? parsedOutput.data.kind : parsedInput.success ? parsedInput.data.kind : null);
  const failureCode = parsedOutput.success && parsedOutput.data.status === 'error'
    ? parsedOutput.data.errorCode
    : generation?.status === 'failed' || generation?.status === 'dead'
      ? generation.errorCode
      : null;
  const isGenerating = state === 'input-streaming'
    || state === 'input-available'
    || generation?.status === 'pending'
    || generation?.status === 'generating';
  const isSucceeded = generation?.status === 'succeeded';
  const isFailed = Boolean(failureCode || statusQuery.isError);

  const statusText = isFailed
    ? (failureCode
      ? getChatErrorMessage({ code: failureCode })
      : getChatErrorMessage(statusQuery.error))
    : isGenerating
      ? 'Génération en cours'
      : errorText
        ? getChatErrorMessage(new Error(errorText))
        : 'Préparation de la génération';

  const cardClassName = cn(
    'flex w-full max-w-xl items-center gap-3 rounded-[var(--radius-surface-2xl)] border border-border/70 bg-card/90 px-3.5 py-3 text-left transition',
    isSucceeded && 'cursor-pointer hover:-translate-y-0.5 hover:border-border hover:bg-card hover:shadow-[0_18px_40px_rgba(15,23,42,0.07)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
    // En cours : même allure neutre que la carte terminée, mais en mode désactivé
    // (atténuée, non interactive) — pas d'anneau/ombre active.
    isGenerating && 'cursor-default opacity-65',
    isFailed && 'border-destructive/25 bg-destructive/5',
  );

  const statusIcon = isFailed
    ? <XCircleIcon className="size-3.5 shrink-0 text-destructive" aria-hidden />
    : <Loader2Icon className="size-3.5 shrink-0 animate-spin text-muted-foreground" aria-hidden />;

  if (isSucceeded) {
    return (
      <div
        aria-label={`Consulter ${title}`}
        className={cardClassName}
        onClick={() => void handlePreview()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            void handlePreview();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <DocumentGenerationKindIcon kind={kind} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{title}</p>
          <Badge
            className="mt-1.5 gap-1 border-emerald-500/20 bg-emerald-500/[0.07] px-2 py-0.5 text-[11px] font-medium tracking-wide text-emerald-800 shadow-none dark:text-emerald-100"
            variant="outline"
          >
            {isOpeningPreview
              ? <Loader2Icon className="size-3 animate-spin text-emerald-600" aria-hidden />
              : <CheckCircle2Icon className="size-3 text-emerald-600 dark:text-emerald-400" aria-hidden />}
            Terminé
          </Badge>
          {generation.result.revisionNumber ? (
            <p className="mt-1 text-xs text-muted-foreground">
              Version {generation.result.revisionNumber}
            </p>
          ) : null}
        </div>
        <Button
          aria-label={`Télécharger ${title}`}
          className="shrink-0"
          disabled={isDownloading || isOpeningPreview}
          onClick={(event) => void handleDownload(event)}
          size="sm"
          type="button"
          variant="outline"
        >
          {isDownloading
            ? <Loader2Icon className="size-3.5 animate-spin" data-icon="inline-start" />
            : <DownloadIcon className="size-3.5" data-icon="inline-start" />}
          Télécharger
        </Button>
        {generation.result.generatedDocumentId && generation.result.revisionNumber && accepted?.conversationId ? (
          <ChatDocumentVersionsMenu
            conversationId={accepted.conversationId}
            generatedDocumentId={generation.result.generatedDocumentId}
            title={title}
          />
        ) : null}
      </div>
    );
  }

  return (
    <div
      aria-busy={isGenerating}
      aria-live="polite"
      className={cardClassName}
      role="status"
    >
      <DocumentGenerationKindIcon kind={kind} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-foreground">{title}</p>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          {statusIcon}
          <span>{statusText}</span>
        </div>
      </div>
    </div>
  );
}
